/**
 * Image Compression Utility
 *
 * Provides client-side image compression before upload to reduce file sizes
 * and improve upload speeds, especially on mobile networks.
 *
 * Uses browser-image-compression library.
 */

import imageCompression from 'browser-image-compression';

/**
 * Default compression options
 */
const DEFAULT_OPTIONS = {
  maxSizeMB: 1,              // Max file size in MB
  maxWidthOrHeight: 1920,    // Max dimension (maintains aspect ratio)
  useWebWorker: true,        // Use web worker for better performance
  initialQuality: 0.85,      // Start with 85% quality
};

/**
 * Compressible image types
 */
const COMPRESSIBLE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

/**
 * Minimum file size to trigger compression (500KB)
 */
const MIN_SIZE_FOR_COMPRESSION = 500 * 1024;

/**
 * Format bytes as human-readable text
 * @param {number} bytes - Number of bytes
 * @param {boolean} si - Use SI units (powers of 1000) vs binary (powers of 1024)
 * @param {number} dp - Decimal places
 * @returns {string} Formatted string
 */
export function formatFileSize(bytes, si = true, dp = 1) {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + ' ' + units[u];
}

/**
 * Check if a file is a compressible image
 * @param {File} file - The file to check
 * @returns {boolean} True if the file can be compressed
 */
export function isCompressibleImage(file) {
  return file && COMPRESSIBLE_TYPES.includes(file.type);
}

/**
 * Compress an image file
 *
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options (optional)
 * @param {number} options.maxSizeMB - Maximum file size in MB (default: 1)
 * @param {number} options.maxWidthOrHeight - Maximum dimension (default: 1920)
 * @param {number} options.initialQuality - Initial quality 0-1 (default: 0.85)
 * @param {string} options.fileType - OUTPUT MIME type. Defaults to the input's type,
 *   so existing callers keep round-tripping PNG as PNG. Pass 'image/jpeg' when the
 *   consumer only accepts JPEG (patient profile photos) and the PNG/WebP input must
 *   be transcoded rather than merely shrunk.
 * @param {number} options.minSizeForCompression - OUR option, not the library's.
 *   Byte floor below which compression is skipped entirely (default: 500KB). Pass 0
 *   to force the pipeline to run on even a tiny file — the only way a small PNG
 *   actually becomes a JPEG, since the size-skip returns the ORIGINAL file untouched.
 *   Destructured out here so it never reaches browser-image-compression, which would
 *   ignore an unknown key silently.
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<{file: File, originalSize: number, compressedSize: number, savings: string}>}
 */
export async function compressImage(file, options = {}, onProgress = null) {
  // Return original if not a compressible image
  if (!isCompressibleImage(file)) {
    console.log(`📁 File is not a compressible image (${file.type}), skipping compression`);
    return {
      file,
      originalSize: file.size,
      compressedSize: file.size,
      savings: '0',
      skipped: true,
      reason: 'not-image'
    };
  }

  // `minSizeForCompression` is ours; everything else is the library's.
  const { minSizeForCompression = MIN_SIZE_FOR_COMPRESSION, ...libOptions } = options;

  const originalSize = file.size;

  // Skip compression for small files
  if (originalSize < minSizeForCompression) {
    console.log(`📁 File is already small (${formatFileSize(originalSize)}), skipping compression`);
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      savings: '0',
      skipped: true,
      reason: 'already-small'
    };
  }

  console.log(`🖼️ Original image size: ${formatFileSize(originalSize)}`);

  // Merge options with defaults
  const compressionOptions = {
    ...DEFAULT_OPTIONS,
    ...libOptions,
    // Output type: the caller's explicit choice wins, otherwise keep the input's
    // type (the behaviour every pre-existing caller relies on).
    fileType: libOptions.fileType || file.type,
    onProgress: onProgress
  };

  try {
    const compressedFile = await imageCompression(file, compressionOptions);
    const compressedSize = compressedFile.size;
    const savings = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);

    console.log(`✅ Compressed image: ${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} (${savings}% reduction)`);

    return {
      file: compressedFile,
      originalSize,
      compressedSize,
      savings,
      skipped: false
    };
  } catch (error) {
    console.error('❌ Image compression failed:', error);
    // Return original file if compression fails
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      savings: '0',
      skipped: true,
      reason: 'error',
      error
    };
  }
}

/**
 * Compress an image file with simple interface (just returns the file)
 * Use this for simple drop-in replacement
 *
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options (optional)
 * @returns {Promise<File>} Compressed file or original if not compressible
 */
export async function compressImageSimple(file, options = {}) {
  const result = await compressImage(file, options);
  return result.file;
}

export default {
  compressImage,
  compressImageSimple,
  isCompressibleImage,
  formatFileSize
};
